// §1–§3 of the client's SOURCES tab, verbatim. §4, the numbered bibliography, is authored
// in `data/sources.translations.ts` instead, since every metal sheet cites it — the same
// file records the transcription rules these strings follow.
//
// Only `intro` is ours, hence its `_placeholder_` id.
export default {
  intro: {
    id: 'rm_footer_sources_placeholder_intro',
    defaultMessage:
      'Every figure on this site is traceable. This document collects the studies, datasets and reports the campaign draws on: the headline figures and their sources, the research behind each metal story, how every score on a metal sheet was calculated, and — last — the numbered bibliography that each superscript in the site points to.',
  },
  lastUpdate: {
    id: 'rm_footer_sources_last_update',
    defaultMessage: 'Last update: September 2026',
  },

  // The four sections, in the client's order.
  sectionHero: {
    id: 'rm_footer_sources_section_hero',
    defaultMessage: 'Campaign hero data',
  },
  sectionStories: {
    id: 'rm_footer_sources_section_stories',
    defaultMessage: 'Metal stories',
  },
  sectionMethod: {
    id: 'rm_footer_sources_section_method',
    defaultMessage: 'Metal sheets scoring methodology',
  },
  sectionBibliography: {
    id: 'rm_footer_sources_section_bibliography',
    defaultMessage: 'Metal sheets bibliography',
  },
  // The client's own heading for the unnumbered tail of §4.
  methodOnlyTitle: {
    id: 'rm_footer_sources_method_only_title',
    defaultMessage:
      'Sources used in scoring methodology only (not cited in metal sheet texts)',
  },

  // Recurring block labels, authored once and reused across §3, so they cannot drift.
  labelMeasures: {
    id: 'rm_footer_sources_label_measures',
    defaultMessage: 'What it measures',
  },
  labelWhatItIs: {
    id: 'rm_footer_sources_label_what_it_is',
    defaultMessage: 'What it is',
  },
  labelPrimarySources: {
    id: 'rm_footer_sources_label_primary_sources',
    defaultMessage: 'Primary sources',
  },
  labelPrimarySource: {
    id: 'rm_footer_sources_label_primary_source',
    defaultMessage: 'Primary source',
  },
  labelSecondarySource: {
    id: 'rm_footer_sources_label_secondary_source',
    defaultMessage:
      'Secondary source (in case no covered by the primary sources)',
  },
  labelCalculation: {
    id: 'rm_footer_sources_label_calculation',
    defaultMessage: 'Calculation',
  },
  labelLimitations: {
    id: 'rm_footer_sources_label_limitations',
    defaultMessage: 'Limitations',
  },
  labelLimitation: {
    id: 'rm_footer_sources_label_limitation',
    defaultMessage: 'Limitation',
  },
  labelScale: {
    id: 'rm_footer_sources_label_scale',
    defaultMessage: 'Scale',
  },
  labelHowToRead: {
    id: 'rm_footer_sources_label_how_to_read',
    defaultMessage: 'How to read it',
  },
  labelRiskLevels: {
    id: 'rm_footer_sources_label_risk_levels',
    defaultMessage: 'Risk levels',
  },
  labelCriticalNote: {
    id: 'rm_footer_sources_label_critical_note',
    defaultMessage: 'Critical note',
  },
  labelDefinedScoring: {
    id: 'rm_footer_sources_label_defined_scoring',
    defaultMessage: 'Defined scoring by Back Market',
  },
  labelMetalList: {
    id: 'rm_footer_sources_label_metal_list',
    defaultMessage: 'List of metals',
  },
  labelThresholds: {
    id: 'rm_footer_sources_label_thresholds',
    defaultMessage: 'Approximate quantitative thresholds',
  },

  // ─── §1 Campaign hero data ────────────────────────────────────────────────────────
  heroClaim1: {
    id: 'rm_footer_sources_hero_claim_1',
    defaultMessage:
      'Making a 200 g smartphone requires 266.7 kg of materials extracted from the Earth — more than 1,000 times its weight.',
  },
  heroClaim2: {
    id: 'rm_footer_sources_hero_claim_2',
    defaultMessage:
      '80% of a smartphone’s carbon footprint is generated before its first minute of use.',
  },
  heroClaim2Note: {
    id: 'rm_footer_sources_hero_claim_2_note',
    defaultMessage:
      'Manufacturing — from raw material extraction and mining to component assembly and transport — is by far the largest contributor to a device’s environmental impact.',
  },
  heroClaim3: {
    id: 'rm_footer_sources_hero_claim_3',
    defaultMessage:
      'Nearly 99% of a smartphone’s raw material footprint is locked in during manufacturing.',
  },
  heroClaim4: {
    id: 'rm_footer_sources_hero_claim_4',
    defaultMessage:
      'Keeping a single phone in productive use for 10 years instead of replacing it every 2.5 years can avoid approximately 757 kg of raw material extraction, roughly the weight of a small car.',
  },
  heroClaim4Note: {
    id: 'rm_footer_sources_hero_claim_4_note',
    defaultMessage:
      'Repairing and refurbishing that device requires only about one twentieth of the raw materials needed to manufacture its replacements.',
  },
  heroClaim5: {
    id: 'rm_footer_sources_hero_claim_5',
    defaultMessage:
      'Thousands of children work the mines that supply the metals for our upgrade.',
  },
  heroClaim5Note: {
    id: 'rm_footer_sources_hero_claim_5_note',
    defaultMessage:
      '[ADEME 2024](https://librairie.ademe.fr/economie-circulaire-et-dechets/7713-etude-numerique-et-metaux.html) estimates that artisanal and informal mining accounts for approximately 10–20% of global cobalt production. Child labour has been documented in some of these mines.',
  },
  heroClaim5Lead: {
    id: 'rm_footer_sources_hero_claim_5_lead',
    defaultMessage:
      'The presence of child labour in Congolese cobalt mining has also been documented by multiple studies and investigations, including the following sources:',
  },
  heroClaim6: {
    id: 'rm_footer_sources_hero_claim_6',
    defaultMessage:
      'Superpowers bully, extort, and violate international law to seize critical minerals',
  },
  // Cut to one sentence by the client, from a three-sentence paragraph.
  heroClaim6Disclaimer: {
    id: 'rm_footer_sources_hero_claim_6_disclaimer',
    defaultMessage:
      'Although strong words are employed to characterize these circumstances, we emphasize that no court has officially ruled that these illegal acts were committed.',
  },
  heroClaim7: {
    id: 'rm_footer_sources_hero_claim_7',
    defaultMessage:
      'Up to 1.4 billion people live in regions where toxic metal pollution puts soil, food and health at risk.',
  },
  heroClaim7Note: {
    id: 'rm_footer_sources_hero_claim_7_note',
    defaultMessage:
      'Mining is a major contributor, and the metals involved include cobalt, copper and nickel, all used in smartphones.',
  },
  heroClaim8: {
    id: 'rm_footer_sources_hero_claim_8',
    defaultMessage:
      'Metal mining exposes an estimated 23 million people to dangerous concentrations of toxic waste along riverbanks.',
  },

  // ─── §1 / §2 references, unnumbered ───────────────────────────────────────────────
  // Printed in full and unnumbered, as the client has them: most are not in §4 at all, and
  // inventing [74]+ would break the numbering every `SourceMarker` depends on.
  refAdeme2022: {
    id: 'rm_footer_sources_ref_ademe_2022',
    defaultMessage:
      'ADEME, EFL Consulting, DDemain, & Bureau Veritas. (2022). Assessment of the environmental impact of a set of refurbished products [Study, 181 pp.]. [https://librairie.ademe.fr/economie-circulaire-et-dechets/5833-assessment-of-the-environmental-impact-of-a-set-of-refurbished-products.html](https://librairie.ademe.fr/economie-circulaire-et-dechets/5833-assessment-of-the-environmental-impact-of-a-set-of-refurbished-products.html)',
  },
  // Claim 1's study, under the client's own annotation: 156 pp. and a delta, not 181 pp.
  refAdeme2022Footprint: {
    id: 'rm_footer_sources_ref_ademe_2022_footprint',
    defaultMessage:
      'ADEME, EFL Consulting, DDemain, & Bureau Veritas. (2022). Assessment of the environmental impact of a set of refurbished products [Study, 156 pp., difference between impact including all uses and impact excluding use]. [https://librairie.ademe.fr/economie-circulaire-et-dechets/5833-assessment-of-the-environmental-impact-of-a-set-of-refurbished-products.html](https://librairie.ademe.fr/economie-circulaire-et-dechets/5833-assessment-of-the-environmental-impact-of-a-set-of-refurbished-products.html)',
  },
  refShiftProject2021: {
    id: 'rm_footer_sources_ref_shift_project_2021',
    defaultMessage:
      'Ferreboeuf, H., Efoui-Hess, M., & Verne, X. (2021, March). Environmental impacts of digital technology: 5-year trends and 5G governance: Updated prospective scenarios for the environmental impacts of global digital technology, and proposals for a rational 5G deployment [Analysis note]. The Shift Project. [https://theshiftproject.org/app/uploads/2025/02/Environmental-impacts-of-digital-technology-5-year-trends-and-5G-governance_March2021.pdf](https://theshiftproject.org/app/uploads/2025/02/Environmental-impacts-of-digital-technology-5-year-trends-and-5G-governance_March2021.pdf)',
  },
  // Rewritten by the client from carbon to raw material; their trailing `%20` is dropped.
  refLifespanScenario: {
    id: 'rm_footer_sources_ref_lifespan_scenario',
    defaultMessage:
      'The calculation is based on [ADEME 2022 study](https://librairie.ademe.fr/economie-circulaire-et-dechets/5833-assessment-of-the-environmental-impact-of-a-set-of-refurbished-products.html), which measures raw material cost per year of use. The key insight: manufacturing is the extraction cost. Spread that cost over 10 years instead of 2.5 years, and the annual impact drops dramatically. A phone kept for 2.5 years costs 106.7 kg of resources per year. A phone kept for 10 years (accounting for two battery replacements, one screen repair, and one refurbishment cycle) costs 30.9 kg of resources per year. Over a decade, that difference, projected cumulatively, equals approximately 757 kg of avoided extraction per device. It’s not speculative. It’s based on lifecycle analysis data showing that extending use is the most effective way to reduce extraction demand.',
  },
  refUnicefDrc2022: {
    id: 'rm_footer_sources_ref_unicef_drc_2022',
    defaultMessage:
      'UNICEF Democratic Republic of the Congo (2022). Secondary analysis of MICS 2018 data. [https://www.unicef.org/executiveboard/media/22266/file/2024-PL12-Democratic-Republic-Congo-draft-CPD-EN-2024-06-10.pdf](https://www.unicef.org/executiveboard/media/22266/file/2024-PL12-Democratic-Republic-Congo-draft-CPD-EN-2024-06-10.pdf)',
  },
  refAmnesty2016: {
    id: 'rm_footer_sources_ref_amnesty_2016',
    defaultMessage:
      'Amnesty International & Afrewatch (2016). This Is What We Die For: Human Rights Abuses in the Democratic Republic of the Congo Power the Global Trade in Cobalt. AFR 62/3183/2016. [https://www.amnesty.org/en/documents/afr62/3183/2016/en/](https://www.amnesty.org/en/documents/afr62/3183/2016/en/)',
  },
  refKara2018: {
    id: 'rm_footer_sources_ref_kara_2018',
    defaultMessage:
      'Siddharth Kara, an adjunct lecturer at Harvard Kennedy School, a visiting scientist at Harvard School of Public Health, and the author of Cobalt Red, reported in a 2018 article for The Guardian: “Based on the data I gathered, I estimate there are more than 255,000 creuseurs mining cobalt in DRC, at least 35,000 of whom are children, some as young as six.” Siddharth Kara, “Is your phone tainted by the misery of the 35,000 children in Congo’s mines?,” The Guardian, 12 October 2018, [https://www.theguardian.com/global-development/2018/oct/12/phone-misery-children-congo-cobalt-mines-drc](https://www.theguardian.com/global-development/2018/oct/12/phone-misery-children-congo-cobalt-mines-drc).',
  },
  refNiarchos2026: {
    id: 'rm_footer_sources_ref_niarchos_2026',
    defaultMessage:
      'Nicolas Niarchos, “Inside the Brutal Center of Copper and Cobalt Mining in the Congo,” Literary Hub, 22 January 2026, [https://lithub.com/inside-the-brutal-center-of-copper-and-cobalt-mining-in-the-congo/](https://lithub.com/inside-the-brutal-center-of-copper-and-cobalt-mining-in-the-congo/).',
  },
  refPblSocial2025: {
    id: 'rm_footer_sources_ref_pbl_social_2025',
    defaultMessage:
      'PBL Netherlands Environmental Assessment Agency (2025). Social impacts of mining critical raw materials. Challenges and entry points for governance. PBL publication number: 5740. The Hague. [https://www.pbl.nl/system/files/document/2025-06/pbl-2025-social-impacts-of-mining-critical-raw-materials-5740.pdf](https://www.pbl.nl/system/files/document/2025-06/pbl-2025-social-impacts-of-mining-critical-raw-materials-5740.pdf)',
  },
  // The client's two sub-bullets on the PBL entry, joined. The broken grammar is theirs —
  // transcribed as written and flagged, not repaired.
  refPblSocial2025Note: {
    id: 'rm_footer_sources_ref_pbl_social_2025_note',
    defaultMessage:
      'Reports cases of Free, Prior and Informed Consent (FPIC) international rights being breached reported in following reports. Documents the unequal distribution of value, environmental damage, social disruption and human-rights risks in critical-mineral supply chains.',
  },
  refYaleMyanmar2025: {
    id: 'rm_footer_sources_ref_yale_myanmar_2025',
    defaultMessage:
      'Yale Environment 360 (2025). Myanmar’s Rare Earth Mining Boom. [https://e360.yale.edu/features/myanmar-rare-earth-mining](https://e360.yale.edu/features/myanmar-rare-earth-mining)',
  },
  refNolen2026: {
    id: 'rm_footer_sources_ref_nolen_2026',
    defaultMessage:
      'Nolen, S. (2026, March 16). U.S. considers withholding H.I.V. aid unless Zambia expands minerals access. The New York Times. [https://www.nytimes.com/2026/03/16/health/zambia-hiv-aid-minerals-trump.html](https://www.nytimes.com/2026/03/16/health/zambia-hiv-aid-minerals-trump.html)',
  },
  // The three wires the client cites identically in §1 and §2. The other three they byline
  // in §1 and attribute to "Reuters." in §2, so those are two strings each, below.
  refReutersGreenland2025: {
    id: 'rm_footer_sources_ref_reuters_greenland_2025',
    defaultMessage:
      'Reuters. (2025, March 12). Greenland chooses pro-business, independence parties in potential boost for Trump. Reuters. [https://www.reuters.com/world/europe/greenland-votes-pro-business-independence-parties-trump-interest-looms-2025-03-12/](https://www.reuters.com/world/europe/greenland-votes-pro-business-independence-parties-trump-interest-looms-2025-03-12/)',
  },
  refReutersUkraine2025: {
    id: 'rm_footer_sources_ref_reuters_ukraine_2025',
    defaultMessage:
      'Reuters. (2025, February 3). Trump says he wants Ukraine to supply U.S. with rare earths. Reuters. [https://www.reuters.com/markets/commodities/trump-says-he-wants-ukraine-supply-us-with-rare-earths-2025-02-03/](https://www.reuters.com/markets/commodities/trump-says-he-wants-ukraine-supply-us-with-rare-earths-2025-02-03/)',
  },
  refReutersCongoRwanda2025: {
    id: 'rm_footer_sources_ref_reuters_congo_rwanda_2025',
    defaultMessage:
      'Reuters. (2025, December 4). Congo, Rwanda leaders affirm commitment to Trump-backed peace deal. Reuters. [https://www.reuters.com/world/africa/trump-hosts-congo-rwanda-leaders-latest-push-peace-2025-12-04/](https://www.reuters.com/world/africa/trump-hosts-congo-rwanda-leaders-latest-push-peace-2025-12-04/)',
  },
  refRussellAustralia2025: {
    id: 'rm_footer_sources_ref_russell_australia_2025',
    defaultMessage:
      'Russell, C. (2025, October 21). U.S.-Australia critical minerals deal underscores the gap to China. Reuters. [https://www.reuters.com/markets/commodities/us-australia-critical-minerals-deal-underscores-gap-china-2025-10-21/](https://www.reuters.com/markets/commodities/us-australia-critical-minerals-deal-underscores-gap-china-2025-10-21/)',
  },
  refScheyderPentagon2025: {
    id: 'rm_footer_sources_ref_scheyder_pentagon_2025',
    defaultMessage:
      'Scheyder, E. (2025, July 15). Pentagon to keep investing in U.S. critical minerals projects, defense official says. Reuters. [https://www.reuters.com/world/china/pentagon-keep-working-with-us-rare-earths-projects-us-defense-official-says-2025-07-15/](https://www.reuters.com/world/china/pentagon-keep-working-with-us-rare-earths-projects-us-defense-official-says-2025-07-15/)',
  },
  refJacksonReprieve2025: {
    id: 'rm_footer_sources_ref_jackson_reprieve_2025',
    defaultMessage:
      'Jackson, L. (2025, October 30). U.S. gets rare earth reprieve from China, but not rollback. Reuters. [https://www.reuters.com/sustainability/climate-energy/china-agrees-one-year-rare-earth-export-deal-issue-settled-says-trump-2025-10-30/](https://www.reuters.com/sustainability/climate-energy/china-agrees-one-year-rare-earth-export-deal-issue-settled-says-trump-2025-10-30/)',
  },
  refScienceSoil2025: {
    id: 'rm_footer_sources_ref_science_soil_2025',
    defaultMessage:
      'Science Journal 2025, Global soil pollution by toxic metals threatens agriculture and human health. [https://www.science.org/doi/10.1126/science.adr5214](https://www.science.org/doi/10.1126/science.adr5214)',
  },
  refPblEnvironmental2024: {
    id: 'rm_footer_sources_ref_pbl_environmental_2024',
    defaultMessage:
      'PBL Netherlands Environmental Assessment Agency (2024). Environmental impacts of extraction and processing of raw materials for the energy transition. PBL publication number: 5364. The Hague. [https://www.pbl.nl/system/files/document/2024-02/PBL-2024-Environmental-impacts-of-extraction-and-processing-of-raw-materials-for-the-energy-transition-5364.pdf](https://www.pbl.nl/system/files/document/2024-02/PBL-2024-Environmental-impacts-of-extraction-and-processing-of-raw-materials-for-the-energy-transition-5364.pdf)',
  },

  // ─── §2 Metal stories ─────────────────────────────────────────────────────────────
  storyHuman: {
    id: 'rm_footer_sources_story_human',
    defaultMessage: 'Human Impact — Cobalt',
  },
  storyEnvironmental: {
    id: 'rm_footer_sources_story_environmental',
    defaultMessage: 'Environmental Impact — Gold',
  },
  storyGeopolitical: {
    id: 'rm_footer_sources_story_geopolitical',
    defaultMessage: 'Geopolitical Impact — Rare Earths Elements',
  },
  refGenocideWatch2025: {
    id: 'rm_footer_sources_ref_genocide_watch_2025',
    defaultMessage:
      'Harris, G., Kibalya, E., & Gruber, L. (2025, June 3). Special report: Conflict minerals in the DR Congo. Genocide Watch, Great Lakes Team. [https://www.genocidewatch.com/single-post/special-report-conflict-minerals-in-the-drc](https://www.genocidewatch.com/single-post/special-report-conflict-minerals-in-the-drc)',
  },
  refGuardianCobaltLawsuit2019: {
    id: 'rm_footer_sources_ref_guardian_cobalt_lawsuit_2019',
    defaultMessage:
      'The Guardian. (2019, December 16). Apple and Google named in US lawsuit over Congolese child cobalt mining deaths. [https://www.theguardian.com/global-development/2019/dec/16/apple-and-google-named-in-us-lawsuit-over-congolese-child-cobalt-mining-deaths](https://www.theguardian.com/global-development/2019/dec/16/apple-and-google-named-in-us-lawsuit-over-congolese-child-cobalt-mining-deaths)',
  },
  refJacobs2018: {
    id: 'rm_footer_sources_ref_jacobs_2018',
    defaultMessage:
      'Jacobs, H. (2018, May 7). Inside “iPhone City,” the massive Chinese factory town where half of the world’s iPhones are produced. Business Insider. [https://www.businessinsider.com/apple-iphone-factory-foxconn-china-photos-tour-2018-5](https://www.businessinsider.com/apple-iphone-factory-foxconn-china-photos-tour-2018-5)',
  },
  refGreene2018: {
    id: 'rm_footer_sources_ref_greene_2018',
    defaultMessage:
      'Greene, J. (2018, Fall). The condition of the working class in Shenzhen. Dissent Magazine. [https://dissentmagazine.org/article/condition-working-class-shenzhen-peasant-workers-authoritarian-consumerism/](https://dissentmagazine.org/article/condition-working-class-shenzhen-peasant-workers-authoritarian-consumerism/)',
  },
  refSunwoda: {
    id: 'rm_footer_sources_ref_sunwoda',
    defaultMessage:
      'Sunwoda Electronic Co., Ltd. (n.d.). Sunwoda. Retrieved September 4, 2026, from [https://en.sunwoda.com/](https://en.sunwoda.com/)',
  },
  refStopCarcinogensCobalt: {
    id: 'rm_footer_sources_ref_stop_carcinogens_cobalt',
    defaultMessage:
      'STOP Carcinogens at Work. “The Facts on Cobalt Compounds.” [https://stopcarcinogensatwork.eu/fact/cobalt-compounds/](https://stopcarcinogensatwork.eu/fact/cobalt-compounds/)',
  },
  refOshaCobaltDust: {
    id: 'rm_footer_sources_ref_osha_cobalt_dust',
    defaultMessage:
      'Occupational Safety and Health Administration. “Cobalt Dust.” [https://www.osha.gov/etools/sawmills/plant-wide-hazards/health-hazards/cobalt-dust](https://www.osha.gov/etools/sawmills/plant-wide-hazards/health-hazards/cobalt-dust)',
  },
  refAppleRecycledCobalt2023: {
    id: 'rm_footer_sources_ref_apple_recycled_cobalt_2023',
    defaultMessage:
      'Apple. “Apple will use 100 percent recycled cobalt in batteries by 2025.” April 13, 2023. [https://www.apple.com/newsroom/2023/04/apple-will-use-100-percent-recycled-cobalt-in-batteries-by-2025/](https://www.apple.com/newsroom/2023/04/apple-will-use-100-percent-recycled-cobalt-in-batteries-by-2025/)',
  },
  refGuardianWestPapua2016: {
    id: 'rm_footer_sources_ref_guardian_west_papua_2016',
    defaultMessage:
      'The Guardian. (2016, November 2). West Papua’s verdant heartlands devastated by mine waste: In pictures. [https://www.theguardian.com/global-development/gallery/2016/nov/02/west-papua-indonesia-verdant-heartlands-devastated-by-mine-waste-in-pictures](https://www.theguardian.com/global-development/gallery/2016/nov/02/west-papua-indonesia-verdant-heartlands-devastated-by-mine-waste-in-pictures)',
  },
  refWwfFreeport: {
    id: 'rm_footer_sources_ref_wwf_freeport',
    defaultMessage:
      'WWF. (n.d.). Freeport mine. Retrieved September 4, 2026, from [https://wwf.panda.org/discover/knowledge_hub/where_we_work/new_guinea_forests/problems_forests_new_guinea/mining_new_guinea/papua_freeport_mine/](https://wwf.panda.org/discover/knowledge_hub/where_we_work/new_guinea_forests/problems_forests_new_guinea/mining_new_guinea/papua_freeport_mine/)',
  },
  refUnepToxicTrail2023: {
    id: 'rm_footer_sources_ref_unep_toxic_trail_2023',
    defaultMessage:
      'UNEP (2023). Ending the toxic trail of small-scale gold mining. UNEP.org, 16 August 2023. [https://www.unep.org/news-and-stories/story/ending-toxic-trail-small-scale-gold-mining](https://www.unep.org/news-and-stories/story/ending-toxic-trail-small-scale-gold-mining)',
  },
  // Re-bylined to §4 [13]'s short form; the id keeps the old byline, so the fr follows.
  refWwfGold2021: {
    id: 'rm_footer_sources_ref_schon_blume_2021',
    defaultMessage:
      'WWF (2021). The Impact of Gold: Sustainability Aspects in the Gold Supply-Chains and Switzerland’s Role as a Gold Hub. [https://www.wwf.ch/sites/default/files/doc-2021-11/2021-11-WWF-The-Impact-of-Gold.pdf](https://www.wwf.ch/sites/default/files/doc-2021-11/2021-11-WWF-The-Impact-of-Gold.pdf)',
  },
  refZeng2025: {
    id: 'rm_footer_sources_ref_zeng_2025',
    defaultMessage:
      'Zeng, Z., Huang, L., Wu, G., Li, M., Wang, H., Zhao, M., & Zhang, H. (2025). Research advancements in the treatment of wastewater containing pollutants in printed circuit board production. Environmental Science: Water Research & Technology, 11(5), 1038–1058. [https://doi.org/10.1039/D4EW00827H](https://doi.org/10.1039/D4EW00827H)',
  },
  refEuParliament2025: {
    id: 'rm_footer_sources_ref_eu_parliament_2025',
    defaultMessage:
      'European Parliament. (2025, July 10). Commission must tackle China’s export restrictions on rare earth elements. [https://www.europarl.europa.eu/news/en/press-room/20250704IPR29456/commission-must-tackle-china-s-export-restrictions-on-rare-earth-elements](https://www.europarl.europa.eu/news/en/press-room/20250704IPR29456/commission-must-tackle-china-s-export-restrictions-on-rare-earth-elements)',
  },
  refIeaGcmo2026: {
    id: 'rm_footer_sources_ref_iea_gcmo_2026',
    defaultMessage:
      'IEA (2026). Global Critical Minerals Outlook 2026. International Energy Agency, Paris. Landing page: [https://www.iea.org/reports/global-critical-minerals-outlook-2026](https://www.iea.org/reports/global-critical-minerals-outlook-2026) [https://iea.blob.core.windows.net/assets/19c60aa0-ee07-4a20-a138-33c967ac5008/GlobalCriticalMineralsOutlook2026.pdf](https://iea.blob.core.windows.net/assets/19c60aa0-ee07-4a20-a138-33c967ac5008/GlobalCriticalMineralsOutlook2026.pdf)',
  },
  refReutersAustralia2025: {
    id: 'rm_footer_sources_ref_reuters_australia_2025',
    defaultMessage:
      'Reuters. (2025, October 21). U.S.-Australia critical minerals deal underscores the gap to China. Reuters. [https://www.reuters.com/markets/commodities/us-australia-critical-minerals-deal-underscores-gap-china-2025-10-21/](https://www.reuters.com/markets/commodities/us-australia-critical-minerals-deal-underscores-gap-china-2025-10-21/)',
  },
  refReutersPentagon2025: {
    id: 'rm_footer_sources_ref_reuters_pentagon_2025',
    defaultMessage:
      'Reuters. (2025, July 15). Pentagon to keep investing in U.S. critical minerals projects, defense official says. Reuters. [https://www.reuters.com/world/china/pentagon-keep-working-with-us-rare-earths-projects-us-defense-official-says-2025-07-15/](https://www.reuters.com/world/china/pentagon-keep-working-with-us-rare-earths-projects-us-defense-official-says-2025-07-15/)',
  },
  refReutersReprieve2025: {
    id: 'rm_footer_sources_ref_reuters_reprieve_2025',
    defaultMessage:
      'Reuters. (2025, October 30). U.S. gets rare earth reprieve from China, but not rollback. Reuters. [https://www.reuters.com/sustainability/climate-energy/china-agrees-one-year-rare-earth-export-deal-issue-settled-says-trump-2025-10-30/](https://www.reuters.com/sustainability/climate-energy/china-agrees-one-year-rare-earth-export-deal-issue-settled-says-trump-2025-10-30/)',
  },

  // ─── §3 Metal sheets scoring methodology ──────────────────────────────────────────
  // The client's full-capital subsection headings, set sentence case as everywhere else.

  // 3.1
  method1Title: {
    id: 'rm_footer_sources_method_1_title',
    defaultMessage: 'Top mining countries (% of world mine production)',
  },
  method1Measures: {
    id: 'rm_footer_sources_method_1_measures',
    defaultMessage:
      'The countries that mine the most of this material and each country’s share of total world mine production. For co-product or by-product metals (eg. gallium, indium, germanium) that are not mined directly but recovered during refinery processing, this section refers to top producing countries at the processing/refinery stage rather than mining; footnotes are included on individual metal sheets where this distinction applies',
  },
  method1RoleBgs: {
    id: 'rm_footer_sources_method_1_role_bgs',
    defaultMessage: 'Secondary cross-check for mine production by country.',
  },
  method1Calculation: {
    id: 'rm_footer_sources_method_1_calculation',
    defaultMessage:
      'Country production value divided by world total (same source, same unit, same year). Applied consistently across all metals.',
  },
  method1HowToRead1: {
    id: 'rm_footer_sources_method_1_how_to_read_1',
    defaultMessage:
      '% represents a country’s mine production as a share of the world total. Figures are based on 2024 estimates or 2023 actuals.',
  },
  method1HowToRead2: {
    id: 'rm_footer_sources_method_1_how_to_read_2',
    defaultMessage:
      '~ denotes approximate figures, either because of limited data or because percentages have been rounded.',
  },

  // 3.2
  method2Title: {
    id: 'rm_footer_sources_method_2_title',
    defaultMessage: 'Estimated quantity in smartphone',
  },
  method2Measures: {
    id: 'rm_footer_sources_method_2_measures',
    defaultMessage:
      'Order-of-magnitude estimate of how much of this material is present in a typical smartphone. Not a precise figure. 4 tiers: Major (>1g) / Significant (0.1-1.3g) / Low (13-130mg) / Trace (<13mg, invisible to the naked eye).',
  },
  method2RoleGomez: {
    id: 'rm_footer_sources_method_2_role_gomez',
    defaultMessage:
      'Role: component-level ppm data by ICP spectrometry across 4 smartphone models (2009-2019).',
  },
  method2RoleBrunning: {
    id: 'rm_footer_sources_method_2_role_brunning',
    defaultMessage: 'Role: whole-device estimates used to complement [2].',
  },
  method2Calculation: {
    id: 'rm_footer_sources_method_2_calculation',
    defaultMessage:
      'Raw ppm data converted by us to gram order-of-magnitude ranges, using average smartphone mass approximately 150g and typical component masses from published teardown literature. 4 tiers defined by us: Major >1g / Significant 0.1-1.3g / Low 13-130mg / Trace <13mg.',
  },
  method2Limitations: {
    id: 'rm_footer_sources_method_2_limitations',
    defaultMessage:
      'Based on 2009-2019 smartphone models — current formulations may differ. Gomez et al. measures at component level; Compound Interest at device level — these two bases are not directly comparable.',
  },

  // 3.3
  method3Title: {
    id: 'rm_footer_sources_method_3_title',
    defaultMessage: 'Smartphone parts names',
  },
  method3WhatItIs: {
    id: 'rm_footer_sources_method_3_what_it_is',
    defaultMessage:
      'Narrative summary of what this material does inside a smartphone, grouped by component.',
  },
  // The client's §3.3 entry for [2] runs into a copy-paste corruption; transcribed up to
  // the break, flagged rather than silently completed.
  method3RoleGomez: {
    id: 'rm_footer_sources_method_3_role_gomez',
    defaultMessage:
      'Role: ICP spectrometry component-level ppm data spanning four smartphone models (2009–2019).',
  },
  method3Scoring1: {
    id: 'rm_footer_sources_method_3_scoring_1',
    defaultMessage: 'Trace = <13 mg per smartphone, invisible to the naked eye',
  },
  method3Scoring2: {
    id: 'rm_footer_sources_method_3_scoring_2',
    defaultMessage: 'Low = 13–130 mg, approximately a grain of rice',
  },
  method3Scoring3: {
    id: 'rm_footer_sources_method_3_scoring_3',
    defaultMessage: 'Significant = >130 mg–1.3 g',
  },
  method3Scoring4: {
    id: 'rm_footer_sources_method_3_scoring_4',
    defaultMessage: 'Several grams = >1.3–10 g',
  },
  method3Scoring5: {
    id: 'rm_footer_sources_method_3_scoring_5',
    defaultMessage: 'Major = >10 g',
  },
  method3Limitations: {
    id: 'rm_footer_sources_method_3_limitations',
    defaultMessage:
      'Narrative text only. Reflects 2009-2019 smartphone models.',
  },

  // 3.4 — metals ADEME 2024 covers
  method4Title: {
    id: 'rm_footer_sources_method_4_title',
    defaultMessage:
      'Scoring methodology for metals covered by ADEME 2024 study',
  },
  method4MetalList: {
    id: 'rm_footer_sources_method_4_metal_list',
    defaultMessage:
      'Aluminium, Antimony, Cobalt, Copper, Gallium, Germanium, Gold, Indium, Lithium, Magnesium, Manganese, Nickel, Palladium, Platinum, Rare Earth Elements Family, Ruthenium, Silicon*, Silver, Tantalum, Tin, Tungsten, Zinc.',
  },
  method4Intro: {
    id: 'rm_footer_sources_method_4_intro',
    defaultMessage:
      'These scores measure each metal’s relative level of risk compared with the 25 metals studied in the ADEME report, on a scale from 1 (comparatively lower risk) to 5 (comparatively higher risk). A score of 1/5 therefore does not mean that extraction is impact-free, but rather that it is less harmful than the extraction of other metals, within a mining industry whose overall environmental, human and geopolitical impacts remain significant.',
  },
  method4EnvTitle: {
    id: 'rm_footer_sources_method_4_env_title',
    defaultMessage: 'Environmental impact score (1-5)',
  },
  method4EnvMeasures: {
    id: 'rm_footer_sources_method_4_env_measures',
    defaultMessage:
      'A score from 1 to 5 measuring the environmental damage caused by extracting and refining this material, cradle-to-gate.',
  },
  method4EnvScale1: {
    id: 'rm_footer_sources_method_4_env_scale_1',
    defaultMessage:
      '1 = A score of 1 means that the metal’s environmental footprint is low to average within the mining industry. It does not mean that its absolute impact is low. Mining itself is a high-impact industry, so even a score of 1 represents a significant environmental footprint.',
  },
  method4EnvScale5: {
    id: 'rm_footer_sources_method_4_env_scale_5',
    defaultMessage:
      '5 = A score of 5 means an exceptionally high footprint within the mining industry.',
  },
  // The client writes "environmental scores … reproduced directly" as the calculation for
  // all three of these scores, human and geopolitical included. Transcribed as written and
  // flagged — it reads as a copy artifact, but it is not ours to correct.
  method4EnvCalculation: {
    id: 'rm_footer_sources_method_4_env_calculation',
    defaultMessage:
      'environmental scores for the 25 ADEME metals, reproduced directly',
  },
  method4HumanTitle: {
    id: 'rm_footer_sources_method_4_human_title',
    defaultMessage: 'Human impact score (1-5)',
  },
  method4HumanMeasures: {
    id: 'rm_footer_sources_method_4_human_measures',
    defaultMessage:
      'A score from 1 to 5 measuring human development conditions in the countries that produce this material. Reflects the situation of the populations bearing the social cost of extraction — not individual corporate practices.',
  },
  method4HumanScale1: {
    id: 'rm_footer_sources_method_4_human_scale_1',
    defaultMessage:
      '1 = production dominated by very high-HDI countries (strong labour rights, stable conditions).',
  },
  method4HumanScale5: {
    id: 'rm_footer_sources_method_4_human_scale_5',
    defaultMessage:
      '5 = production almost exclusively in very low-HDI countries with documented child labour, forced labour, or armed conflict.',
  },
  method4GeoTitle: {
    id: 'rm_footer_sources_method_4_geo_title',
    defaultMessage: 'Geopolitical impact score (1-5)',
  },
  method4GeoMeasures: {
    id: 'rm_footer_sources_method_4_geo_measures',
    defaultMessage:
      'A score from 1 to 5 measuring the governance fragility of the countries that dominate production. Reflects structural political conditions.',
  },
  method4GeoCriticalNote: {
    id: 'rm_footer_sources_method_4_geo_critical_note',
    defaultMessage:
      'Export restrictions are shown as contextual information but are deliberately excluded from this score. The score reflects structural governance conditions, not individual political decisions.',
  },
  method4GeoScale1: {
    id: 'rm_footer_sources_method_4_geo_scale_1',
    defaultMessage:
      '1 = production concentrated in highly stable, well-governed countries.',
  },
  method4GeoScale5: {
    id: 'rm_footer_sources_method_4_geo_scale_5',
    defaultMessage:
      '5 = production dominated by countries with very poor governance or active conflicts.',
  },
  method4SupplyTitle: {
    id: 'rm_footer_sources_method_4_supply_title',
    defaultMessage: 'Supply risk score',
  },
  method4SupplyMeasures: {
    id: 'rm_footer_sources_method_4_supply_measures',
    defaultMessage:
      'Our overall assessment of the risk that global demand will outpace available supply, combining geological depletion signals, demand growth projections, and supply chain concentration data.',
  },
  method4SupplyRiskLevels: {
    id: 'rm_footer_sources_method_4_supply_risk_levels',
    defaultMessage:
      'Critical / High / Moderate / Low or declining demand / Not formally assessed.',
  },
  method4SupplyRoleIea: {
    id: 'rm_footer_sources_method_4_supply_role_iea',
    defaultMessage: 'Role: supply-demand gap projections and demand forecasts.',
  },
  method4SupplyRoleAdeme: {
    id: 'rm_footer_sources_method_4_supply_role_ademe',
    defaultMessage: 'Role: ADEME criticality scores.',
  },
  method4SupplyRoleGreenIt: {
    id: 'rm_footer_sources_method_4_supply_role_green_it',
    defaultMessage:
      'Role: geological depletion dates based on reserve-to-production ratios.',
  },
  method4SupplyRoleUsgs: {
    id: 'rm_footer_sources_method_4_supply_role_usgs',
    defaultMessage: 'Role: reserves and production data.',
  },
  method4SupplyCalculation: {
    id: 'rm_footer_sources_method_4_supply_calculation',
    defaultMessage:
      'Editorial synthesis from 3 signal types: (1) Geological: GreenIT depletion dates based on reserve-to-production ratios. (2) Demand-side: IEA GCMO 2025 supply-demand gap projections. (3) Institutional: ADEME criticality scores. No single source publishes this combined indicator.',
  },

  // 3.5 — metals ADEME 2024 does not cover
  method5Title: {
    id: 'rm_footer_sources_method_5_title',
    defaultMessage:
      'Scoring methodology for metals not covered by ADEME 2024 study',
  },
  method5Intro: {
    id: 'rm_footer_sources_method_5_intro',
    defaultMessage:
      'This methodology explains how Back Market calculated the environmental, human, geopolitical, and supply scores for raw materials not included in the ADEME 2024 study.',
  },
  method5MetalList: {
    id: 'rm_footer_sources_method_5_metal_list',
    defaultMessage:
      'Arsenic, Graphite, Barium, Boron, Cadmium, Chromium, Hafnium, Iron, Lead, Niobium, Strontium, Titanium, Vanadium, Zirconium.',
  },
  method5EnvTitle: {
    id: 'rm_footer_sources_method_5_env_title',
    defaultMessage: 'A. Environmental score',
  },
  method5EnvStep1: {
    id: 'rm_footer_sources_method_5_env_step_1',
    defaultMessage:
      'We used the Graedel 2015 study, specifically [Appendix Table S6](https://www.pnas.org/action/downloadSupplement?doi=10.1073%2Fpnas.1500415112&file=pnas.1500415112.sapp.pdf).',
  },
  method5EnvStep2: {
    id: 'rm_footer_sources_method_5_env_step_2',
    defaultMessage:
      'For each metal, we identified its Critical Environmental Implications Score.',
  },
  method5EnvStep3: {
    id: 'rm_footer_sources_method_5_env_step_3',
    defaultMessage: 'We converted the score to a rating as follows:',
  },
  // Asterisk, hyphen and marker verbatim — their `[10]` is PBL social where it means [9].
  method5EnvGraphiteException: {
    id: 'rm_footer_sources_method_5_env_graphite_exception',
    defaultMessage:
      '*Exception for Graphite - Graphite is covered by neither ADEME 2024 nor Graedel et al. (2015), so Back Market estimates its environmental score at 2/5 by comparison with ADEME-rated materials. PBL documents dust, chemical effluents and processing residues, while a peer-reviewed cradle-to-gate study using Chinese industrial data estimated 9.6 kg CO₂e/kg of battery-grade natural graphite. This is an internal proxy, not an official ADEME score, and carries greater uncertainty because impacts vary significantly by production process and energy mix. [3,10, Journal of Cleaner Production (2022)]',
  },
  refEngelsGraphite2022: {
    id: 'rm_footer_sources_ref_engels_graphite_2022',
    defaultMessage:
      'Journal of Cleaner Production (2022), Engels, P., Cerda, F., Dettmer, T., Frey, C., Hentschel, J., Herrmann, C., Mirfabrikikar, T. & Schueler, M. “Life cycle assessment of natural graphite production for lithium-ion battery anodes based on industrial primary data.” Journal of Cleaner Production, 336, 130474. [https://doi.org/10.1016/j.jclepro.2022.130474](https://doi.org/10.1016/j.jclepro.2022.130474)',
  },
  method5HumanTitle: {
    id: 'rm_footer_sources_method_5_human_title',
    defaultMessage: 'B. Human score',
  },
  method5HumanStep1: {
    id: 'rm_footer_sources_method_5_human_step_1',
    defaultMessage: 'We identified the countries where each metal is mined.',
  },
  method5HumanStep2: {
    id: 'rm_footer_sources_method_5_human_step_2',
    defaultMessage:
      'We obtained the Human Development Index for each country (year of study: 2023) and calculated a weighted average based on each country’s share of global mining.',
  },
  method5HumanStep3: {
    id: 'rm_footer_sources_method_5_human_step_3',
    defaultMessage: 'We converted the result to a rating as follows:',
  },
  method5GeoTitle: {
    id: 'rm_footer_sources_method_5_geo_title',
    defaultMessage: 'C. Geopolitical score',
  },
  // The client's sentence hyperlinks "World Bank Worldwide Governance Indicators" to the
  // PNAS supplement — the Graedel URL from §5.A. Rendered as plain text with the WGI cited
  // properly beneath, rather than shipping a link whose label contradicts its target.
  method5GeoIntro: {
    id: 'rm_footer_sources_method_5_geo_intro',
    defaultMessage: 'We used the World Bank Worldwide Governance Indicators.',
  },
  method5GeoStep1: {
    id: 'rm_footer_sources_method_5_geo_step_1',
    defaultMessage: 'We identified the countries where each metal is mined.',
  },
  method5GeoStep2: {
    id: 'rm_footer_sources_method_5_geo_step_2',
    defaultMessage:
      'For each country, we calculated the average of the six governance indicators.',
  },
  method5GeoStep3: {
    id: 'rm_footer_sources_method_5_geo_step_3',
    defaultMessage:
      'We calculated a weighted average based on each country’s share of global mining.',
  },
  method5GeoStep4: {
    id: 'rm_footer_sources_method_5_geo_step_4',
    defaultMessage: 'We converted the result to a rating as follows:',
  },
  method5SupplyTitle: {
    id: 'rm_footer_sources_method_5_supply_title',
    defaultMessage: 'D. Supply score',
  },
  method5SupplyStep1: {
    id: 'rm_footer_sources_method_5_supply_step_1',
    defaultMessage:
      'We calculated the average mining production for 2023 and 2024.',
  },
  method5SupplyStep2: {
    id: 'rm_footer_sources_method_5_supply_step_2',
    defaultMessage:
      'We divided this average by the world’s remaining reserves.',
  },
  method5SupplyStep3: {
    id: 'rm_footer_sources_method_5_supply_step_3',
    defaultMessage:
      'We converted the resulting production to reserves ratio to a rating as follows:',
  },

  // The four conversion tables. Each row is the client's line verbatim; the dots beside it
  // are `ScoreCircles`, echoing the value the row already states.
  method5EnvT1: {
    id: 'rm_footer_sources_method_5_env_t1',
    defaultMessage: '0 to 20: 1 out of 5',
  },
  method5EnvT2: {
    id: 'rm_footer_sources_method_5_env_t2',
    defaultMessage: 'More than 20 to 40: 2 out of 5',
  },
  method5EnvT3: {
    id: 'rm_footer_sources_method_5_env_t3',
    defaultMessage: 'More than 40 to 60: 3 out of 5',
  },
  method5EnvT4: {
    id: 'rm_footer_sources_method_5_env_t4',
    defaultMessage: 'More than 60 to 80: 4 out of 5',
  },
  method5EnvT5: {
    id: 'rm_footer_sources_method_5_env_t5',
    defaultMessage: 'More than 80 to 100: 5 out of 5',
  },
  method5HumanT1: {
    id: 'rm_footer_sources_method_5_human_t1',
    defaultMessage: '0 to 0.2: 5 out of 5',
  },
  method5HumanT2: {
    id: 'rm_footer_sources_method_5_human_t2',
    defaultMessage: 'More than 0.2 to 0.4: 4 out of 5',
  },
  method5HumanT3: {
    id: 'rm_footer_sources_method_5_human_t3',
    defaultMessage: 'More than 0.4 to 0.6: 3 out of 5',
  },
  method5HumanT4: {
    id: 'rm_footer_sources_method_5_human_t4',
    defaultMessage: 'More than 0.6 to 0.8: 2 out of 5',
  },
  method5HumanT5: {
    id: 'rm_footer_sources_method_5_human_t5',
    defaultMessage: 'More than 0.8 to 1: 1 out of 5',
  },
  method5GeoT1: {
    id: 'rm_footer_sources_method_5_geo_t1',
    defaultMessage: '0 to 20: 5 out of 5',
  },
  method5GeoT2: {
    id: 'rm_footer_sources_method_5_geo_t2',
    defaultMessage: 'More than 20 to 40: 4 out of 5',
  },
  method5GeoT3: {
    id: 'rm_footer_sources_method_5_geo_t3',
    defaultMessage: 'More than 40 to 60: 3 out of 5',
  },
  method5GeoT4: {
    id: 'rm_footer_sources_method_5_geo_t4',
    defaultMessage: 'More than 60 to 80: 2 out of 5',
  },
  method5GeoT5: {
    id: 'rm_footer_sources_method_5_geo_t5',
    defaultMessage: 'More than 80 to 100: 1 out of 5',
  },
  method5SupplyT1: {
    id: 'rm_footer_sources_method_5_supply_t1',
    defaultMessage: '0 to 1%: 1 out of 5',
  },
  method5SupplyT2: {
    id: 'rm_footer_sources_method_5_supply_t2',
    defaultMessage: 'More than 1% to 2%: 2 out of 5',
  },
  method5SupplyT3: {
    id: 'rm_footer_sources_method_5_supply_t3',
    defaultMessage: 'More than 2% to 4%: 3 out of 5',
  },
  method5SupplyT4: {
    id: 'rm_footer_sources_method_5_supply_t4',
    defaultMessage: 'More than 4% to 8%: 4 out of 5',
  },
  method5SupplyT5: {
    id: 'rm_footer_sources_method_5_supply_t5',
    defaultMessage: 'More than 8%: 5 out of 5',
  },

  // 3.6
  method6Title: {
    id: 'rm_footer_sources_method_6_title',
    defaultMessage: 'Methodology for recycling level indicator for all metals',
  },
  method6Measures: {
    id: 'rm_footer_sources_method_6_measures',
    defaultMessage:
      'Indicates how much of total consumption currently comes from recycled material (secondary supply) versus freshly mined ore (primary supply).',
  },
  method6RoleIea: {
    id: 'rm_footer_sources_method_6_role_iea',
    defaultMessage:
      'Role: primary source for recycled share data and recycling viability assessments.',
  },
  method6RoleJrc: {
    id: 'rm_footer_sources_method_6_role_jrc',
    defaultMessage:
      'Role: supplementary recycling data for materials not covered by IEA.',
  },
  method6RoleAdeme: {
    id: 'rm_footer_sources_method_6_role_ademe',
    defaultMessage:
      'Role: supplementary recycling data and qualitative assessments for specific metals.',
  },
  method6RoleUnep: {
    id: 'rm_footer_sources_method_6_role_unep',
    defaultMessage:
      'Note: The report provides global-average estimates from a 2011 report. The table combines published literature with expert estimates, so they should be presented as indicative rather than as current measured recycling rates.',
  },
  // The client's ranges overlap (2/5 ≈ 5–15% against 3/5 ≈ 10–30%) and each line trails a
  // stray list separator. The separators are dropped as formatting; the overlap is theirs
  // and is flagged, not silently smoothed.
  method6T5: {
    id: 'rm_footer_sources_method_6_t5',
    defaultMessage: '5/5 ≈ >50%',
  },
  method6T4: {
    id: 'rm_footer_sources_method_6_t4',
    defaultMessage: '4/5 ≈ 30-50%',
  },
  method6T3: {
    id: 'rm_footer_sources_method_6_t3',
    defaultMessage: '3/5 ≈ 10–30%',
  },
  method6T2: {
    id: 'rm_footer_sources_method_6_t2',
    defaultMessage: '2/5 ≈ 5–15%',
  },
  method6T1: {
    id: 'rm_footer_sources_method_6_t1',
    defaultMessage: '1/5 ≈ <5%',
  },
  method6TNa: {
    id: 'rm_footer_sources_method_6_t_na',
    defaultMessage: 'N/A = not assessed or not applicable',
  },
  method6Limitation: {
    id: 'rm_footer_sources_method_6_limitation',
    defaultMessage:
      'Not a precise measurement. Reflects qualitative assessments in the source literature combined with available quantitative data.',
  },
} as const
